import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useInView } from "react-intersection-observer";
import { NextHead } from "@components/nextHead";

import { ItineraryThemeContextProvider } from "@pageComponents/itineraries/themes";
import { MapDrawerContextProvider } from "@pageComponents/itineraries/mapDrawer/context";

import { Hero } from "@pageComponents/itineraries/hero";
import { Intro } from "@pageComponents/itineraries/intro";
import { Subnav } from "@pageComponents/itineraries/subnav";
import { Day } from "@components/_pages/itineraries/day";
import { Banner } from "@pageComponents/itineraries/banner";
import { MapDrawer } from "@pageComponents/itineraries/mapDrawer";

import { api } from "@queries/itineraries/api";
import { tagular } from "@cohesion/tagular";

export default function ItineraryPageTemplate({
  meta,
  editorial,
  media,
  days,
}) {
  const introSectionRef = useRef(null);

  // Subnav Day Navigation
  const dayRefs = useRef([]);
  const [activeDay, setActiveDay] = useState(null);

  const setDayRef = (element, index) => {
    dayRefs.current[index] = element;
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries =>
        entries.forEach(entry => {
          const { id } = entry.target;

          if (entry.isIntersecting) {
            setActiveDay(id.replace("d-", ""));
          }
        }),
      { threshold: 0.1 }
    );

    dayRefs.current.forEach(ref => {
      if (ref) observer.observe(ref);
    });

    const { current } = dayRefs;
    return () => {
      current.forEach(ref => observer.unobserve(ref));
    };
  }, [dayRefs]);

  const scrollToDay = esid => {
    setTimeout(() => {
      setActiveDay(esid);
    }, 1000);

    const el = document.querySelector(`#d-${esid}`);
    el.scrollIntoView({ behavior: "smooth" });
  };

  // CTA Banner Section Tagular Tracking
  const [bannerSectionRef, inView] = useInView({
    threshold: 1,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      tagular("view", {
        webElement: {
          elementType: "BUTTON",
          location: "ITINERARYFOOTER",
          name: "CTA",
          position: "0",
          text: "KEEP EXPLORING",
        },
      });
    }
  }, [inView]);

  return (
    <ItineraryThemeContextProvider>
      <MapDrawerContextProvider>
        <NextHead image={{ url: meta.frontmatter.image }}>
          <meta
            key="og:image"
            property="og:image"
            content={meta.frontmatter.image}
          />

          <title>{meta.frontmatter.title}</title>
          <meta key="title" name="title" content={meta.frontmatter.title} />
          <meta
            key="og:title"
            property="og:title"
            content={meta.frontmatter.title}
          />
          <meta
            key="twitter:title"
            name="twitter:title"
            content={meta.frontmatter.title}
          />

          <link key="canonical" rel="canonical" href={meta.frontmatter.url} />
          <meta key="og:url" property="og:url" content={meta.frontmatter.url} />

          <meta
            key="description"
            name="description"
            content={meta.frontmatter.description}
          />
          <meta
            key="og:description"
            property="og:description"
            content={meta.frontmatter.description}
          />
          <meta
            key="twitter:description"
            name="twitter:description"
            content={meta.frontmatter.description}
          />
        </NextHead>

        <div className="-mt-16">
          <Hero
            heading={meta.destination.title}
            subheading={meta.destination.parent.title}
            media={media}
            handleNextClick={() => {
              introSectionRef.current.scrollIntoView({ behavior: "smooth" });
              tagular("click", {
                actionOutcome: "SELECT",
                outboundUrl: "null",
                webElement: {
                  elementType: "BUTTON",
                  location: "ITINERARYHEADER",
                  name: "ARROW",
                  position: "0",
                  text: "CONTINUE",
                },
              });
            }}
          />

          {/* Intro Section */}
          <div ref={introSectionRef}>
            <Intro
              heading={meta.destination.title}
              html={editorial}
              media={media}
            />
          </div>
        </div>

        {/* Days Section */}
        <div>
          {days.length > 1 && (
            <div className="sticky top-0 z-20">
              <Subnav
                heading={meta.destination.title}
                items={days.map(day => ({
                  esid: day.esid,
                }))}
                handleClick={scrollToDay}
                activeDay={activeDay}
              />
            </div>
          )}

          {days.map((day, i) => (
            <Day
              key={day.esid}
              day={day}
              dayIndex={i}
              setDaySectionRef={setDayRef}
            />
          ))}
        </div>

        {/* CTA Banner Section */}
        <div ref={bannerSectionRef}>
          <Banner
            heading="Discover Even More Things To See & Do"
            className="-mb-1"
            cta={{
              label: "Keep Exploring",
              href: `/${meta.destination.slug}`,
              onClick: () => {
                tagular("click", {
                  actionOutcome: "SELECT",
                  outboundUrl: `/${meta.destination.slug}`,
                  webElement: {
                    elementType: "BUTTON",
                    location: "ITINERARYFOOTER",
                    name: "CTA",
                    position: "0",
                    text: "KEEP EXPLORING",
                  },
                });
              },
            }}
          />
        </div>

        <MapDrawer />
      </MapDrawerContextProvider>
    </ItineraryThemeContextProvider>
  );
}

export async function getServerSideProps({ params }) {
  const { slug } = params;

  const data = await api({ slug });

  if (!data) {
    return { notFound: true };
  }

  return {
    props: {
      ...data,
      disableAlert: true,
      disableModals: true,
    },
  };
}

ItineraryPageTemplate.propTypes = {
  meta: PropTypes.shape({
    scheme: PropTypes.number.isRequired,
    destination: PropTypes.shape({
      title: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      parent: PropTypes.shape({
        title: PropTypes.string.isRequired,
      }),
    }).isRequired,
    frontmatter: PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  title: PropTypes.string.isRequired,
  editorial: PropTypes.string.isRequired,
  media: PropTypes.arrayOf(
    PropTypes.shape({
      images: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string,
          alt: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  days: PropTypes.arrayOf(
    PropTypes.shape({
      esid: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      editorial: PropTypes.string.isRequired,
      pois: PropTypes.arrayOf(
        PropTypes.shape({
          esid: PropTypes.string,
          title: PropTypes.string,
          slug: PropTypes.string,
          editorial: PropTypes.string,
          image: PropTypes.shape({
            src: PropTypes.string,
            credit: PropTypes.string,
            alt: PropTypes.string,
          }),
          meta: PropTypes.shape({
            address: PropTypes.string,
            coords: PropTypes.shape({
              lat: PropTypes.number,
              lon: PropTypes.number,
            }),
            tags: PropTypes.arrayOf(
              PropTypes.shape({ slug: PropTypes.string.isRequired })
            ),
            type: PropTypes.shape({
              slug: PropTypes.string.isRequired,
            }),
            parent: PropTypes.shape({
              slug: PropTypes.string.isRequired,
            }),
          }),
        })
      ).isRequired,
    })
  ).isRequired,
};
